import { passkeySupported$ } from "@mhmo91/passkey/client";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { state as litState } from "lit/decorators.js";
import { merge, takeUntil } from "rxjs";
import { passkeyEnrolled, passkeyPhase } from "../auth/passkey.state.ts";
import { session } from "../auth/session.state.ts";
import { signAction$ } from "../auth/sign.state.ts";
import "@mhmo91/schmancy-lab/settings";
import { localeEndonym } from "@momo/i18n";
import { html } from "lit";
import { LocalizeMixin, setUiLanguage } from "../i18n/localize.ts";
import { effectiveBrandSeedColor } from "../workspace/workspace.state.ts";

/** Each swatch's hex beside the catalog key its NAME reads from — the name
 *  itself resolves in render() and never here, because a string translated at
 *  module evaluation freezes in the language that happened to be active at
 *  boot and every swatch's accessible name goes stale on the next switch. */
const CURATED_ACCENTS = [
	{ nameKey: "settings.accent.violet", hex: "#6750A4" },
	{ nameKey: "settings.accent.ocean", hex: "#1E6FA8" },
	{ nameKey: "settings.accent.forest", hex: "#3B7A57" },
	{ nameKey: "settings.accent.amber", hex: "#C88A2E" },
] as const;

@schmancyElement("app-settings")
export class AppSettings extends LocalizeMixin(SchmancyElement) {
	@litState() private passkeySupported = false;
	@litState() private passkeyPhase: "idle" | "working" | "done" | "error" =
		"idle";
	@litState() private passkeyUid: string | null = null;
	@litState() private enrolledUid: string | null = null;
	override connectedCallback() {
		super.connectedCallback();
		passkeySupported$.pipe(takeUntil(this.disconnecting)).subscribe((value) => {
			this.passkeySupported = value;
		});
		merge(session.$, passkeyEnrolled.$, passkeyPhase.$)
			.pipe(takeUntil(this.disconnecting))
			.subscribe(() => {
				this.passkeyUid = session.value.uid;
				this.enrolledUid = passkeyEnrolled.value;
				this.passkeyPhase = passkeyPhase.value;
			});
	}
	private enrollPasskey() {
		if (this.passkeyPhase === "working") return;
		signAction$.next({ kind: "enrollPasskey", requestId: crypto.randomUUID() });
	}

	override render() {
		return html`
			<schmancy-settings
				.themeLabel=${this.t("settings.theme.label")}
				.closeLabel=${this.t("settings.close")}
				.schemeLabels=${{
					auto: this.t("settings.scheme.auto"),
					light: this.t("settings.scheme.light"),
					dark: this.t("settings.scheme.dark"),
				}}
				.colors=${[
					{
						name: this.t("settings.accent.brand"),
						hex: effectiveBrandSeedColor.get(),
					},
					...CURATED_ACCENTS.map((accent) => ({
						name: this.t(accent.nameKey),
						hex: accent.hex,
					})),
				]}
				.radiusLabel=${this.t("settings.theme.radius")}
				.soundLabel=${this.t("settings.sound.label")}
				.volumeLabel=${this.t("settings.sound.volume")}
				.languageLabel=${this.t("settings.language.label")}
				.language=${this.uiLanguage}
				.languages=${[
					{ value: "de", label: localeEndonym.de },
					{ value: "en", label: localeEndonym.en },
				]}
				@language-change=${(event: CustomEvent<{ value: string }>) => {
					const next = event.detail.value;
					if (next === this.uiLanguage || (next !== "de" && next !== "en"))
						return;
					setUiLanguage(next);
				}}
			>
				${
					this.passkeySupported &&
					this.passkeyUid &&
					session.value.status === "member"
						? html`
					<schmancy-divider></schmancy-divider>
					<passkey-enroll density="row" language=${this.uiLanguage} .phase=${this.passkeyPhase === "idle" || this.passkeyPhase === "done" ? (this.enrolledUid === this.passkeyUid ? "done" : "idle") : this.passkeyPhase} @passkey-enroll=${this.enrollPasskey}></passkey-enroll>
				`
						: ""
				}
			</schmancy-settings>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"app-settings": AppSettings;
	}
}
